import { type CSSProperties, type ReactElement } from 'react';
import { IconCheck } from '@tabler/icons-react';

import { V4 } from '@components/v4/tokens';
import { Commerce } from '@interfaces/general';

import {
  businessTypeOptions,
  getSubTypesByBusinessType,
  type BusinessType as BusinessTypeEnum,
} from '../utils/businessTypeOptions';

interface BusinessTypeV4Props {
  commerce: Commerce;
  handleUpdateValue: (key: keyof Commerce, value: string) => void;
}

/**
 * Step "Tipo de negocio" del onboarding en componentes V4 (DESIGN.md §regla de
 * componentes: tokens V4, sin @plick/ui ni plickTheme legacy).
 *
 * UX: cards seleccionables touch-first en grid responsive en lugar de
 * accordion + radios legacy. Elegir tipo limpia el subtipo (mismo contrato que
 * la versión anterior); el grupo de subtipos aparece debajo al haber tipo.
 * Semántica de radiogroup/radio para accesibilidad — la "card" ES el radio.
 */

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: V4.primary,
};

const helperTextStyle: CSSProperties = {
  margin: '4px 0 0',
  fontSize: 13,
  color: V4.grayUltraDark,
  lineHeight: 1.5,
};

const typeGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: 8,
  marginTop: 12,
};

const subTypeListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 12,
};

interface SelectableCardProps {
  label: string;
  checked: boolean;
  onSelect: () => void;
  /** multiline = subtipos (label largo con descripción); alinea a la izquierda. */
  multiline?: boolean;
}

const SelectableCard = ({ label, checked, onSelect, multiline = false }: SelectableCardProps): ReactElement => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    onClick={onSelect}
    style={{
      display: 'flex',
      alignItems: multiline ? 'flex-start' : 'center',
      justifyContent: multiline ? 'flex-start' : 'center',
      gap: 8,
      width: '100%',
      minHeight: 48,
      padding: '10px 12px',
      borderRadius: 8,
      border: `1.5px solid ${checked ? V4.secondary : V4.grayLight}`,
      background: checked ? V4.grayUltraLight : V4.white,
      color: V4.primary,
      fontSize: 13,
      fontWeight: checked ? 700 : 500,
      lineHeight: 1.4,
      textAlign: multiline ? 'left' : 'center',
      cursor: 'pointer',
      fontFamily: 'inherit',
      transition: 'border-color 140ms ease, background 140ms ease, transform 90ms cubic-bezier(.4,0,.2,1)',
    }}
  >
    {checked ? (
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 18,
          height: 18,
          borderRadius: 9,
          background: V4.secondary,
          color: V4.white,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: multiline ? 1 : 0,
        }}
      >
        <IconCheck size={12} stroke={3} />
      </span>
    ) : null}
    <span>{label}</span>
  </button>
);

const BusinessTypeV4 = ({ commerce, handleUpdateValue }: BusinessTypeV4Props): ReactElement => {
  const selectedBusinessType = commerce.business_type as BusinessTypeEnum;
  const availableSubTypes = selectedBusinessType ? getSubTypesByBusinessType(selectedBusinessType) : [];

  const handleBusinessTypeChange = (value: string): void => {
    handleUpdateValue('business_type', value);
    // Mismo contrato que la versión legacy: cambiar de tipo invalida el subtipo.
    handleUpdateValue('business_sub_type', '');
  };

  return (
    <div data-testid="business-type-v4" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h3 style={sectionTitleStyle}>Tipo de negocio</h3>
        <p style={helperTextStyle}>Ayúdanos a conocer mejor tu negocio para personalizar tu experiencia</p>
        <div role="radiogroup" aria-label="Tipo de negocio" data-testid="business-type-group" style={typeGridStyle}>
          {businessTypeOptions.map((option) => (
            <SelectableCard
              key={option.value}
              label={option.label}
              checked={commerce.business_type === option.value}
              onSelect={() => handleBusinessTypeChange(option.value)}
            />
          ))}
        </div>
      </div>

      {selectedBusinessType && availableSubTypes.length > 0 ? (
        <div>
          <h3 style={sectionTitleStyle}>Elige un subtipo para tu negocio</h3>
          <div
            role="radiogroup"
            aria-label="Subtipo de negocio"
            data-testid="business-subtype-group"
            style={subTypeListStyle}
          >
            {availableSubTypes.map((subType) => (
              <SelectableCard
                key={subType.value}
                label={subType.label}
                checked={commerce.business_sub_type === subType.value}
                onSelect={() => handleUpdateValue('business_sub_type', subType.value)}
                multiline
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BusinessTypeV4;
